import { formatCivilDate, type StatusTone } from '@hue/design-system';
import type { SalesTermStatus } from '../../lib/api.js';

/// A situação pelas datas — e o arquivado do pacote —, no singular da etiqueta e no plural do
/// filtro. Pacote é masculino; promoção, feminino.
export const PACKAGE_STATUS: Record<
  SalesTermStatus,
  { tone: StatusTone; label: string; plural: string }
> = {
  ACTIVE: { tone: 'positive', label: 'Vigente', plural: 'Vigentes' },
  SCHEDULED: { tone: 'warning', label: 'Agendado', plural: 'Agendados' },
  ENDED: { tone: 'neutral', label: 'Encerrado', plural: 'Encerrados' },
  ARCHIVED: { tone: 'neutral', label: 'Arquivado', plural: 'Arquivados' },
};

export const PROMOTION_STATUS: Record<
  Exclude<SalesTermStatus, 'ARCHIVED'>,
  { tone: StatusTone; label: string; plural: string }
> = {
  ACTIVE: { tone: 'positive', label: 'Vigente', plural: 'Vigentes' },
  SCHEDULED: { tone: 'warning', label: 'Agendada', plural: 'Agendadas' },
  ENDED: { tone: 'neutral', label: 'Encerrada', plural: 'Encerradas' },
};

/// "01/10/2026 a 31/10/2026" ou "a partir de 01/08/2026 · ∞ sem data de fim".
export function termText(validFrom: string, validUntil: string | null): string {
  return validUntil
    ? `${formatCivilDate(validFrom)} a ${formatCivilDate(validUntil)}`
    : `a partir de ${formatCivilDate(validFrom)} · ∞ sem data de fim`;
}

/// "Todas as unidades" quando marca todas as que a pessoa enxerga; senão, os nomes.
export function unitsText(
  businessUnitIds: readonly string[],
  units: ReadonlyArray<{ id: string; displayName: string }>,
): string {
  if (businessUnitIds.length === 0) return 'Nenhuma unidade';
  if (units.length > 1 && units.every((unit) => businessUnitIds.includes(unit.id)))
    return 'Todas as unidades';
  const names = units
    .filter((unit) => businessUnitIds.includes(unit.id))
    .map((unit) => unit.displayName);
  const others = businessUnitIds.length - names.length;
  if (others > 0)
    names.push(others === 1 ? '1 outra unidade' : `${String(others)} outras unidades`);
  return names.join(' e ');
}

/// "18,0% de desconto", com uma casa, como a pessoa lê na etiqueta de preço.
export function discountText(normalCents: number, priceCents: number): string {
  const percent = ((normalCents - priceCents) / normalCents) * 100;
  return `${percent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% de desconto`;
}

/// Repartição pelo maior resto, a mesma do servidor: a prévia do valor no saldo bate com o que a
/// venda vai gravar.
export function allocateCents(total: number, weights: readonly number[]): number[] {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightTotal === 0) return weights.map(() => 0);
  const allocations = weights.map((weight) => Math.floor((total * weight) / weightTotal));
  const residual = total - allocations.reduce((sum, value) => sum + value, 0);
  weights
    .map((weight, index) => ({ index, remainder: (total * weight) % weightTotal }))
    .sort((left, right) => right.remainder - left.remainder || left.index - right.index)
    .slice(0, residual)
    .forEach(({ index }) => (allocations[index]! += 1));
  return allocations;
}
